/**
 * Full-page form per creare o modificare un template email outbound.
 * Route: /admin/settings/outbound-email-templates/new | .../:templateId/edit
 */
import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router";
import { useListData } from "react-stately";
import { ArrowLeft } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { Label } from "@/components/base/input/label";
import { MultiSelectBase } from "@/components/base/select/multi-select";
import { SelectItem } from "@/components/base/select/select-item";
import type { SelectItemType } from "@/components/base/select/select";
import {
  useOutboundEmailTemplate,
  useCreateOutboundEmailTemplate,
  useUpdateOutboundEmailTemplate,
  useTemplateCompanies,
  useSetTemplateCompanies,
  useSetTemplateCompaniesById,
} from "@/hooks/api/use-outbound-email-templates";
import { useCompanies } from "@/hooks/api/use-companies";
import type { OutboundEmailTemplateStatus } from "@/types/api/outbound-email-template";
import { toast } from "@/lib/toast-service";
import { RichTextEditorWithTokens } from "../rich-text-editor-with-tokens";

const STATUS_OPTIONS: { id: OutboundEmailTemplateStatus; label: string }[] = [
  { id: "draft", label: "Draft" },
  { id: "active", label: "Active" },
  { id: "inactive", label: "Inactive" },
];

const SETTINGS_TAB_URL = "/admin/settings/outbound-email-templates";

const EDIT_PATH_REGEX = /\/outbound-email-templates\/([^/]+)\/edit$/;
const NEW_PATH_REGEX = /\/outbound-email-templates\/new$/;

export function OutboundEmailTemplateFormPage() {
  const params = useParams<{ templateId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const templateIdFromParams = params.templateId;
  const templateIdFromPath = location.pathname.match(EDIT_PATH_REGEX)?.[1];
  const templateId = templateIdFromParams ?? templateIdFromPath ?? undefined;
  const isNewFromPath = NEW_PATH_REGEX.test(location.pathname);
  const isNew = templateId === "new" || isNewFromPath;

  const { data: template, isLoading: isLoadingTemplate } = useOutboundEmailTemplate(
    isNew ? null : templateId ?? null
  );
  const createTemplate = useCreateOutboundEmailTemplate();
  const updateTemplate = useUpdateOutboundEmailTemplate();
  const setCompaniesById = useSetTemplateCompaniesById();
  const setCompaniesForTemplate = useSetTemplateCompanies(templateId ?? "");
  const { data: assignedCompanies } = useTemplateCompanies(
    !isNew && templateId ? templateId : null
  );
  const { data: companies = [] } = useCompanies({ limit: 500 });

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [status, setStatus] = useState<OutboundEmailTemplateStatus>("draft");
  const [content, setContent] = useState("");
  const [aliases, setAliases] = useState<
    { placeholder: string; table: string; column: string; alias_name: string }[]
  >([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<Set<string>>(new Set());
  const selectedCompanyItems = useListData<SelectItemType>({ initialItems: [] });

  const companyItems: SelectItemType[] = useMemo(
    () =>
      companies.map((c) => ({
        id: c.company_id,
        label: c.company_name,
        supportingText: c.legal_name && c.legal_name !== c.company_name ? c.legal_name : undefined,
      })),
    [companies]
  );

  useEffect(() => {
    if (template) {
      setName(template.name);
      setSlug(template.slug);
      setExcerpt(template.excerpt ?? "");
      setStatus(template.status);
      setContent(template.content ?? "");
      setAliases(template.aliases ?? []);
    } else if (isNew) {
      setName("");
      setSlug("");
      setExcerpt("");
      setStatus("draft");
      setContent("");
      setAliases([]);
    }
  }, [template, isNew]);

  useEffect(() => {
    if (assignedCompanies?.company_ids) {
      setSelectedCompanyIds(new Set(assignedCompanies.company_ids));
    } else if (isNew) {
      setSelectedCompanyIds(new Set());
    }
  }, [assignedCompanies, isNew]);

  // Sincronizza selectedCompanyItems con le aziende assegnate (per il MultiSelect)
  useEffect(() => {
    const ids = isNew || !assignedCompanies ? [] : assignedCompanies.company_ids ?? [];
    const toSelect = companyItems.filter((item) => ids.includes(item.id));
    const current = selectedCompanyItems.items.slice();
    current.forEach((item) => selectedCompanyItems.remove(item.id));
    toSelect.forEach((item) => selectedCompanyItems.append(item));
  }, [isNew, assignedCompanies?.company_ids, companyItems]);

  const companyIdsArray = Array.from(selectedCompanyIds);

  const onCompanyItemInserted = (key: React.Key) => {
    setSelectedCompanyIds((prev) => new Set(prev).add(String(key)));
  };
  const onCompanyItemCleared = (key: React.Key) => {
    setSelectedCompanyIds((prev) => {
      const next = new Set(prev);
      next.delete(String(key));
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isNew) {
        const created = await createTemplate.mutateAsync({
          name,
          slug: slug || undefined,
          excerpt: excerpt || null,
          content,
          aliases,
          status,
        });
        if (companyIdsArray.length > 0) {
          await setCompaniesById.mutateAsync({
            templateId: created.template_id,
            companyIds: companyIdsArray,
          });
        }
        navigate(SETTINGS_TAB_URL);
        return;
      }
      if (templateId && templateId !== "new") {
        await updateTemplate.mutateAsync({
          templateId,
          data: {
            name,
            slug: slug || undefined,
            excerpt: excerpt || null,
            content,
            aliases,
            status,
          },
        });
        await setCompaniesForTemplate.mutateAsync(companyIdsArray);
        navigate(SETTINGS_TAB_URL);
      }
    } catch {
      toast.error("Errore durante il salvataggio del template.");
    }
  };

  if (!isNew && isLoadingTemplate) {
    return (
      <div className="flex items-center justify-center py-24 text-tertiary">
        Caricamento...
      </div>
    );
  }

  if (!isNew && templateId && templateId !== "new" && !template && !isLoadingTemplate) {
    return (
      <div className="space-y-4">
        <p className="text-tertiary">Template non trovato.</p>
        <Link to={SETTINGS_TAB_URL}>
          <Button size="md" color="secondary" iconLeading={ArrowLeft}>
            Torna ai template
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to={SETTINGS_TAB_URL}
          className="rounded-lg p-1 text-tertiary hover:bg-secondary hover:text-primary"
          aria-label="Torna ai template"
        >
          <ArrowLeft className="size-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold text-primary">
            {isNew ? "Nuovo template email" : "Modifica template"}
          </h1>
          <p className="text-sm text-tertiary">
            {isNew
              ? "Compila i campi e usa Insert Token per gli alias nel contenuto."
              : "Modifica nome, contenuto e token. Il contenuto è inserito nel layout email in fase di invio."}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 rounded-xl border border-secondary bg-primary p-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Input
            label="Nome"
            value={name}
            onChange={(v) => setName(v)}
            placeholder="Nome template"
            isRequired
          />
          <Input
            label="Slug (opzionale, auto da nome)"
            value={slug}
            onChange={(v) => setSlug(v)}
            placeholder="slug-template"
          />
        </div>

        <div>
          <Label>Excerpt</Label>
          <textarea
            className="mt-1 w-full rounded-lg border border-secondary bg-primary px-3 py-2 text-sm text-primary min-h-[80px]"
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            placeholder="Breve descrizione"
          />
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="max-w-xs">
            <Select
              label="Status"
              items={STATUS_OPTIONS}
              selectedKey={status}
              onSelectionChange={(key) => setStatus(key as OutboundEmailTemplateStatus)}
              size="md"
            >
              {(item) => (
                <Select.Item key={item.id} id={item.id}>
                  {item.label}
                </Select.Item>
              )}
            </Select>
          </div>
          <div className="min-w-0">
            <MultiSelectBase
              label="Aziende associate"
              hint="Nessuna selezione = template di sistema (disponibile a tutte)."
              items={companyItems}
              selectedItems={selectedCompanyItems}
              onItemInserted={onCompanyItemInserted}
              onItemCleared={onCompanyItemCleared}
              placeholder="Seleziona aziende..."
              size="md"
            >
              {(item) => (
                <SelectItem key={item.id} id={item.id} label={item.label} supportingText={item.supportingText} />
              )}
            </MultiSelectBase>
          </div>
        </div>

        <div className="min-h-[320px]">
          <RichTextEditorWithTokens
            content={content}
            aliases={aliases}
            onContentChange={setContent}
            onAliasesChange={setAliases}
            placeholder="Contenuto email (HTML). Usa 'Insert Token' per alias."
          />
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-secondary pt-6">
          <Link to={SETTINGS_TAB_URL}>
            <Button type="button" size="md" color="secondary">
              Annulla
            </Button>
          </Link>
          <Button
            type="button"
            size="md"
            color="primary"
            isDisabled={
              !name ||
              createTemplate.isPending ||
              updateTemplate.isPending ||
              setCompaniesById.isPending ||
              setCompaniesForTemplate.isPending
            }
            isLoading={
              createTemplate.isPending ||
              updateTemplate.isPending ||
              setCompaniesById.isPending ||
              setCompaniesForTemplate.isPending
            }
            onPress={() => handleSubmit({ preventDefault: () => {} } as React.FormEvent)}
          >
            {isNew ? "Crea template" : "Salva"}
          </Button>
        </div>
      </form>
    </div>
  );
}
